import { useNavigate } from "react-router-dom";
import { selector, useSetRecoilState } from "recoil";
import { toast } from "react-hot-toast";
import CryptoJS from 'crypto-js';
import axios from 'axios';

import { loginAtom, userAtom } from "../atoms/auth.atoms";
import { HOST_GROWME, ROUTES, SECRET_KEY } from "../../helpers/constants";

const useAuthActions = () => {
  let navigate = useNavigate();
  const setSigned = useSetRecoilState(loginAtom);
  const setUserAtomState = useSetRecoilState(userAtom);

  const signInUser = async (email, password, rememberAccount) => {
    setSigned((prevValues) => ({ ...prevValues, loading: true }));

    try {
      const response = await axios.post(`${HOST_GROWME}/login`, {
        username: email,
        password: password
      });

      if (response.data.success && response.data.token) {
        if (rememberAccount) {
          const dataToRemember = JSON.stringify({ email, password });
          const encryptedData = CryptoJS.AES.encrypt(dataToRemember, SECRET_KEY).toString();
          document.cookie = `_sessionStorage=${encryptedData}; path=/`;
        } else {
          const cookieName = '_sessionStorage';
          document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        }

        // Store JWT token in localStorage
        localStorage.setItem('token', response.data.token);

        // Extract user data from backend response
        const userData = response.data.user;
        const data = {
          isSuperAdmin: userData.isSuperAdmin || false,
          isAdmin: userData.isAdmin || false,
          isAdminBasic: userData.isBasic || false,
          clientId: userData.company?.id || "",
          name: userData.name || "",
          email: userData.email || "",
          id: userData.id || userData.username,
          displayName: userData.name || userData.username,
          phone_number: userData.phone_number || "",
          mustChangePassword: userData.mustChangePassword === true
        };

        setUserAtomState((prevValues) => ({
          ...prevValues,
          data: { ...data },
        }));
        setSigned({ loading: false, signed: true });
        toast.success("Inicio de sesión exitoso");
        navigate(ROUTES.CLIENTS.route);
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      let message = error.response?.data?.message || 
                   error.message ||
                   "Inicio de sesión fallido. Por favor, intente de nuevo.";
      toast.error(message);
      setSigned({ loading: false, signed: false, error: error, message });
    }
  };

  const signOut = async () => {
    try {
      localStorage.removeItem('token');
      navigate(ROUTES.LOGIN.route);
      setSigned((prevValues) => ({ ...prevValues, loading: false, signed: false }));
    } catch (error) {
      setSigned((prevValues) => ({ ...prevValues, loading: false, error }));
    }
  };

  const refreshToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        signOut();
        return;
      }

      const response = await axios.post(`${HOST_GROWME}/validateSession`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.valid) {
        // Token is still valid, no need to refresh
        return token;
      } else {
        signOut();
      }
    } catch (error) {
      console.error('Token validation error:', error);
      signOut();
    }
  };

  const validateSession = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }

      const response = await axios.post(`${HOST_GROWME}/validateSession`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return response.data.valid;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  };

  const resetLoginError = () => setSigned((prevValues) => ({ ...prevValues, loading: false }));

  return {
    signInUser,
    signOut,
    refreshToken,
    validateSession,
    resetLoginError
  };
};

const currentSessionSelector = selector({
  key: 'currentSession',
  get: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token found');
    }

    try {
      const response = await axios.post(`${HOST_GROWME}/validateSession`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.data.valid) {
        throw new Error('Session not valid');
      }

      return { token, user: response.data.user };
    } catch (error) {
      localStorage.removeItem('token');
      throw new Error('Session validation failed');
    }
  },
});

export { currentSessionSelector, useAuthActions };
